import { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Typography,
} from '@mui/material';
import api from '../../api/axios';

export interface InviteTarget {
  account: { id: string; name: string };
  email?: string;
  first_name?: string;
  last_name?: string;
}

/** Creates a login without emailing anyone; the admin copies the link and sends it by hand. */
export default function InviteDialog({
  target, onClose, onCreated, onMessage, onError,
}: {
  target: InviteTarget | null;
  onClose: () => void;
  onCreated?: () => void;
  onMessage: (text: string) => void;
  onError: (text: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [link, setLink] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEmail(target?.email ?? '');
    setFirst(target?.first_name ?? '');
    setLast(target?.last_name ?? '');
    setLink(null);
  }, [target]);

  const create = async () => {
    if (!target) return;
    setSaving(true);
    try {
      const resp = await api.post('/invitations/', {
        email: email.trim(),
        first_name: first.trim(),
        last_name: last.trim(),
        account_id: target.account.id,
        send_email: false,
      });
      setLink(resp.data.accept_url);
      onCreated?.();
    } catch (e: any) {
      onError(e.response?.data?.error || 'Failed to create the login.');
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      onMessage('Invite link copied.');
    } catch {
      onError('Could not copy; your browser blocked clipboard access.');
    }
  };

  return (
    <Dialog open={!!target} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        Add member to {target?.account.name}
      </DialogTitle>
      <DialogContent>
        {link ? (
          <>
            <Typography variant="body2" sx={{ mt: 1, mb: 1.5 }}>
              Login created for <b>{email.trim()}</b>. Nothing was emailed.
              Copy the link and send it yourself. It works for 7 days.
            </Typography>
            <TextField fullWidth size="small" value={link} InputProps={{ readOnly: true }} onFocus={(e) => e.target.select()} />
          </>
        ) : (
          <>
            <TextField label="Email" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} sx={{ mt: 1 }} autoFocus />
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField label="First name" fullWidth value={first} onChange={(e) => setFirst(e.target.value)} />
              <TextField label="Last name" fullWidth value={last} onChange={(e) => setLast(e.target.value)} />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {link ? (
          <>
            <Button onClick={onClose}>Done</Button>
            <Button variant="contained" onClick={copy}>Copy link</Button>
          </>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" onClick={create} disabled={saving || !email.trim()}>
              {saving ? <CircularProgress size={20} /> : 'Create login'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
